import React, {useMemo} from 'react'
import {useCategory} from '../../store/hooks/useCategory'
import {Box, BoxProps, Flex, Stack, Text} from '../../vanilla'
import {Link} from '../link'
import {ChevronRightIcon} from '../icons'
import {useContentStore} from '../../store/hooks/useStore'
import {breadcrumbIcon} from './styles.css'
import { linkUtilityMenu } from '../header/styles.css'
import {toSlug} from '../../utils/utils'
import { buildBreadcrumbsStructuredData } from '../../utils/structured-data'
import { StructuredData } from '../seo/structured-data'

export interface Crumb {
  id: string
  name: string
  url?: string
  className?: string
}

export interface BreadcrumbsProps extends BoxProps<'nav'> {
  breadcrumbs?: Array<Crumb>
  categoryId?: string
  search?: string
}

export const Breadcrumbs = ({breadcrumbs, categoryId, search, className, ...rest}: BreadcrumbsProps) => {
  const category = useCategory(categoryId)
  const crumbs: Crumb[] = (breadcrumbs || category?.parentCategoryTree || []) as Crumb[]
  const {routeMap} = useContentStore()

  const home: Crumb = {
    id: 'HOME',
    name: 'Home',
    url: '/',
    className: 'pdp-breadcrumb',
  }
  const searched: Crumb = {
    id: 'SEARCH',
    name: search || '',
  }

  const extractCategoryPathFromRouteMap = (breadcrumbs: Crumb[]) => {
    if (!breadcrumbs?.length) return breadcrumbs

    for (const routePath in routeMap) {
      const dataStr = routeMap[routePath]

      const [_, entityType, entityId] = dataStr.split('|')

      if (entityType === 'CATEGORY') {
        const crumbIndex = breadcrumbs.findIndex((crumb) => crumb.id === entityId)
        if (crumbIndex !== -1 && !Object.prototype.hasOwnProperty.call(breadcrumbs[crumbIndex], 'url'))
          breadcrumbs[crumbIndex] = {...crumbs[crumbIndex], url: routePath}
      }
    }

    return breadcrumbs
  }

  const _crumbs = useMemo(() => extractCategoryPathFromRouteMap(crumbs), [crumbs])

  if (!_crumbs || _crumbs.length === 0) {
    _crumbs.unshift(searched)
  }
  if (_crumbs && _crumbs.length > 0 && _crumbs[0].name != 'Home') {
    _crumbs.unshift(home)
  }

  const structuredData = useMemo(() => {
    if (!_crumbs || _crumbs.length === 0) {
      return []
    }

    return [buildBreadcrumbsStructuredData(_crumbs)]
  }, [_crumbs])

  return (
    <Box as="nav" aria-label="breadcrumb" data-test-selector="breadcrumbs" {...rest}>
      <StructuredData data={structuredData} />
      <Stack
        direction="row"
        align="center"
        as="ol"
        display={['inline-block', 'flex']}
        width="full"
        spacing="0px"
      >
        {_crumbs?.map(({name, id, url, className}, index) => (
          <Flex as="li" key={id} display={['inline-flex', 'flex']} align="center">
            <Text
              variant="text1"
              textDecoration={index !== _crumbs.length - 1 ? 'underline' : 'none'}
              color={'gray1000'} 
              {...(url ? {as: Link, to: url} : {})}
              style={{cursor: url ? 'pointer' : 'unset'}}
              className={className === 'pdp-breadcrumb' && url ? linkUtilityMenu : ''}
              data-test-selector={`breadcrumb-${toSlug(name || '', true)}`}
            >
              {name}
            </Text>
            {index !== _crumbs.length - 1 && (
              <ChevronRightIcon
                className={breadcrumbIcon}
                boxSize="12px"
                marginX="4px"
                display="inline-block"
              />
            )}
          </Flex>
        ))}
      </Stack>
    </Box>
  )
}

